import { computed, ref, type Ref, toValue } from 'vue'
import { t } from './Translation'

export type RejectReason = 'count' | 'total' | 'perFile' | 'duplicate' | 'accept'
export type DedupeKeyFn = (f: File) => string

export interface MessageAttachment {
  id?: string
  name: string
  type: 'image' | 'document'
  thumbnailUrl?: string
  file?: File
  mime?: string
  sizeBytes?: number
}

// Accept refs OR plain values for limits (+ keep others as-is)
type MaybeRef<T> = T | Ref<T>

export interface UseAttachmentsOptions {
  maximumAttachments?: MaybeRef<number | undefined>
  maximumBytesTotal?: MaybeRef<number | undefined>
  maximumBytesPerFile?: MaybeRef<number | null | undefined>
  dedupeKey?: DedupeKeyFn
  acceptedExtensions?: string[]
  acceptedMime?: string[]
  excludedExtensions?: string[]
  excludedMime?: string[]
}

export interface RejectedFile {
  file: File
  reason: RejectReason
}

export interface UseAttachments {
  attachments: Ref<MessageAttachment[]>
  totalBytes: Readonly<Ref<number>>
  count: Readonly<Ref<number>>
  atCountLimit: Readonly<Ref<boolean>>
  atSizeLimit: Readonly<Ref<boolean>>
  addDisabled: Readonly<Ref<boolean>>
  error: Ref<string | null>
  // expose current (reactive) total-bytes limit if the UI needs it
  maxTotalBytes: Readonly<Ref<number>>
  readableBytes: (n: number) => string
  tryAdd: (files: File[] | FileList) => { accepted: MessageAttachment[]; rejected: RejectedFile[] }
  handleFileChange: (e: Event) => void
  removeByKey: (key: string) => void
  clear: () => void
}

const defaultKey: DedupeKeyFn = f => `${f.name}-${f.size}-${f.lastModified}`
const bytesMB = (mb: number) => mb * 1024 * 1024

export function useAttachments(opts: UseAttachmentsOptions = {}): UseAttachments {
  // -------- reactive limits (accept refs or values) --------
  const maxCount = computed<number>(() => {
    const v = toValue(opts.maximumAttachments)
    return v ?? 3
  })

  const maxTotalBytes = computed<number>(() => {
    const v = toValue(opts.maximumBytesTotal)
    return v ?? bytesMB(25)
  })

  const maxFileBytes = computed<number | null>(() => {
    const v = toValue(opts.maximumBytesPerFile)
    // keep original semantics: null = no per-file limit
    return v == null ? null : v
  })

  const dedupeKey = opts.dedupeKey ?? defaultKey
  const acceptExt = opts.acceptedExtensions
  const acceptMime = opts.acceptedMime
  const excludeExt = opts.excludedExtensions
  const excludeMime = opts.excludedMime

  // -------- state --------
  const attachments = ref<MessageAttachment[]>([])
  const error = ref<string | null>(null)

  const sizeOf = (a: MessageAttachment) => a.file?.size ?? a.sizeBytes ?? 0
  const totalBytes = computed(() => attachments.value.reduce((s, a) => s + sizeOf(a), 0))
  const count = computed(() => attachments.value.length)

  const atCountLimit = computed(() => count.value >= maxCount.value)
  const atSizeLimit = computed(() => totalBytes.value >= maxTotalBytes.value)
  const addDisabled = computed(() => atCountLimit.value || atSizeLimit.value)

  const readableBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  }

  const passesAccept = (f: File) => {
    const name = (f.name || '').toLowerCase()
    const mime = (f.type || '').toLowerCase()
    const mimeUnknown = !mime || mime === 'application/octet-stream'

    const normalizeExt = (s: string) => (s.startsWith('.') ? s : '.' + s).toLowerCase()

    const matchesExt = (list?: string[]) =>
      !!list?.length && list.some(ext => name.endsWith(normalizeExt(ext)))

    const matchesMime = (list?: string[]) =>
      !!list?.length &&
      list.some(m => {
        const pattern = m.toLowerCase()
        return mime === pattern || (pattern.endsWith('/') && mime.startsWith(pattern))
      })

    if (matchesExt(excludeExt) || (!mimeUnknown && matchesMime(excludeMime))) {
      return false
    }

    const hasAnyAcceptRules = !!(acceptExt?.length || acceptMime?.length)
    if (!hasAnyAcceptRules) return true

    const extOk = !acceptExt?.length || matchesExt(acceptExt)
    const mimeOk = !acceptMime?.length || mimeUnknown || matchesMime(acceptMime)

    return extOk && mimeOk
  }

  function existingKeySet() {
    const set = new Set<string>()
    attachments.value.forEach(a => {
      if (a.file) set.add(dedupeKey(a.file))
      else set.add(`name:${a.name}`)
    })
    return set
  }

  function tryAdd(files: File[] | FileList) {
    error.value = null
    const list = Array.from(files)
    const keys = existingKeySet()

    let remainingSlots = maxCount.value - attachments.value.length
    let remainingBytes = maxTotalBytes.value - totalBytes.value

    const accepted: MessageAttachment[] = []
    const rejected: RejectedFile[] = []

    for (const file of list) {
      if (remainingSlots <= 0) {
        rejected.push({ file, reason: 'count' })
        continue
      }
      if (!passesAccept(file)) {
        rejected.push({ file, reason: 'accept' })
        continue
      }
      if (maxFileBytes.value != null && file.size > maxFileBytes.value) {
        rejected.push({ file, reason: 'perFile' })
        continue
      }
      const k = dedupeKey(file)
      if (keys.has(k)) {
        rejected.push({ file, reason: 'duplicate' })
        continue
      }
      if (file.size > remainingBytes) {
        rejected.push({ file, reason: 'total' })
        continue
      }

      const att: MessageAttachment = {
        id: k,
        name: file.name,
        type: file.type.startsWith('image/') ? 'image' : 'document',
        file,
        mime: file.type,
        sizeBytes: file.size,
      }
      accepted.push(att)
      keys.add(k)
      remainingSlots -= 1
      remainingBytes -= file.size
    }

    if (accepted.length) attachments.value.push(...accepted)

    if (rejected.length) {
      const reasons = new Set(rejected.map(r => r.reason))
      const parts: string[] = []

      if (reasons.has('count'))
        parts.push(t('chat.question.attachments.validation.maxCount', { max: maxCount.value }))

      if (reasons.has('total'))
        parts.push(
          t('chat.question.attachments.validation.maxTotal', {
            total: readableBytes(maxTotalBytes.value),
          })
        )

      if (reasons.has('perFile') && maxFileBytes.value != null)
        parts.push(
          t('chat.question.attachments.validation.maxPerFile', {
            max: readableBytes(maxFileBytes.value),
          })
        )

      if (reasons.has('accept'))
        parts.push(t('chat.question.attachments.validation.fileTypeNotAllowed'))
      if (reasons.has('duplicate'))
        parts.push(t('chat.question.attachments.validation.duplicatesSkipped'))

      error.value = t('chat.question.attachments.validation.someFilesNotAdded', {
        reasons: parts.join(' • '),
      })
    }

    return { accepted, rejected }
  }

  function handleFileChange(e: Event) {
    const target = e.target as HTMLInputElement
    if (!target.files) return
    tryAdd(target.files)
    target.value = ''
  }

  function removeByKey(key: string) {
    const idx = attachments.value.findIndex(a => {
      if (a.id) return a.id === key
      if (a.file) return defaultKey(a.file) === key
      return `name:${a.name}` === key
    })
    if (idx !== -1) {
      attachments.value.splice(idx, 1)
      if (error.value) error.value = null
    }
  }

  function clear() {
    attachments.value = []
    error.value = null
  }

  return {
    attachments,
    totalBytes,
    count,
    atCountLimit,
    atSizeLimit,
    addDisabled,
    error,
    maxTotalBytes, // <- reactive limit
    readableBytes,
    tryAdd,
    handleFileChange,
    removeByKey,
    clear,
  }
}
